import { useState } from "react";
import {
  Box,
  Button,
  EllipsisVerticalIcon,
  Menu,
  useMenu
} from "@dealer-center/core-components";
import { Locale } from "@dealer-center/ddc-core-utils";
import {
  ResetPasswordModal,
  SCREEN_READER_ONLY,
  collapsedActionsOnly,
  labelledActionsOnly
} from "../../components/UserInfo";

export interface HeaderActionsProps {
  /** Whether a user is selected. Reset Password needs a subject to act on. */
  isSubjectSelected: boolean;
  /**
   * Whether the selected user **is** the operator. Forwarded to the reset dialog, where it selects
   * the unavailability notice's copy register — "your password" only when it really is.
   */
  isSubjectTheOperator?: boolean;
  /**
   * Whether the User Information form differs from the stored record — i.e. whether Save has
   * anything to do.
   *
   * Passed in rather than computed here, because dirtiness is a comparison between
   * `useUserInfoEdits`' overrides and the fetched record, and this component owns neither. The shell
   * already derives it for `usePendingEditsGuard`, so this reads the same value that decides whether
   * navigating away prompts — Save and the prompt can never disagree about whether there are edits.
   *
   * Defaults to `false`, so a caller that forgets it gets an inactive Save rather than one that
   * claims there is something to save.
   */
  isDirty?: boolean;
}

/**
 * FR-020's **exactly three** actions — Reset Password, Delete, Save — positioned outside and above
 * the tab set so they are visible from every tab.
 *
 * **A separate component from My Info's, not a parameterized shared one** (plan Complexity
 * Tracking). Three actions vs two, FR-063's self-delete rule, and a different FR-057
 * disabled-reason treatment. One component driven by flags would render a different action set per
 * caller — more coupling than the ~60 lines it saves — and would drag User Manager's FR-057
 * obligation into My Info's shipped behavior as an untested side-effect.
 *
 * ## What is enabled and what is not
 *
 * | Action | State | Why |
 * |---|---|---|
 * | **Save** | **ENABLED when the form is dirty** | reviewer decision 2026-09-23 |
 * | **Delete** | disabled | no endpoint, and the flow is not a confirmation dialog (FR-061, D-11) |
 * | **Reset Password** | **ENABLED** when a subject is selected | FR-083c, reviewer decision 2026-09-08 |
 *
 * ## Save reflects the form's state and logs — reviewer decision, 2026-09-23
 *
 * **This reverses what this file said until now**, and the previous reasoning is worth keeping
 * visible because it was not wrong, merely outweighed:
 *
 * > Save renders unconditionally inactive — NOT `disabled={!isDirty}`. FR-083 is explicit: with no
 * > endpoint, "a Save that lights up on a dirty form is precisely the
 * > control-that-masquerades-as-working" that FR-057 forbids.
 *
 * The reviewer's position is that **this build is not public**, so a control that reflects its true
 * state is more useful during development than one held inert to avoid misleading an audience that
 * cannot reach it. So Save is now `disabled={!isDirty}` and its `onClick` **logs**; there is still no
 * write endpoint and nothing is persisted.
 *
 * Two consequences, both deliberate:
 *
 * - **The `UM_SAVE_UNAVAILABLE` screen-reader reason is gone**, along with the label key. It said
 *   "Saving changes is not available yet", which becomes a false statement the moment the button
 *   works. A Save that is inactive because nothing has changed needs no explanation — that is the
 *   ordinary meaning of a disabled Save, unlike Delete, which is inactive for a reason the operator
 *   cannot infer and therefore keeps its reason.
 * - **`useUserInfoEdits`' docstring is updated too.** It recorded `isDirty` as "deliberately not
 *   wired to Save's `disabled` yet"; that sentence is now stale and saying so in one place only
 *   would leave the other contradicting the code.
 *
 * ⚠️ **When the endpoint lands, replace the log — do not add a second Save path beside it.** The
 * mutation belongs behind this same `disabled={!isDirty}` gate.
 *
 * **Reset Password is enabled and opens the dialog soft-disabled** (`isWriteAvailable={false}`), so
 * the policy text is readable, both fields are inert, and the dialog states why it cannot act and
 * where the operator still can. This **narrows** FR-083's uniform posture rather than excepting it —
 * it matches what My Info has shipped since 2026-08-27. With no selection there is nobody to act on,
 * so it renders `disabled` with its own reason.
 *
 * **`isSubjectSelected === false` is not reachable from the composed page, and that is a property of
 * the page's layout rather than of this component.** `UserManager` mounts this row only inside its
 * `subject ? (…)` branch; its no-subject arm replaces the whole detail panel with the
 * `UM_NO_SELECTION` message, which says the same thing this control's hidden reason says and says it
 * *visibly*. So the disabled arm is asserted by `HeaderActions.spec.tsx` alone and by no
 * composition test — **do not read a passing suite as evidence the page renders a disabled Reset
 * Password anywhere.** The prop stays because FR-057 attaches a reason to each control's own
 * unavailable state, and the write stories may yet mount this row above the empty panel; if they
 * settle on keeping the header hidden with no subject, the arm and `UM_RESET_PASSWORD_NO_SELECTION`
 * are then dead copy to remove together.
 *
 * `onConfirm` is a **no-op**, matching the shipped page: confirm can never fire while
 * `isWriteAvailable` is false, and a handler that throws or navigates would be a landmine for
 * whoever enables the endpoint. It is the single line Story 6 replaces with the mutation. **Do not
 * "simplify" by dropping `isWriteAvailable`** — leaving it at its `true` default makes the fields
 * live, which makes FR-070's failure-reporting path new UI that the no-op does not provide.
 *
 * ## The disabled reasons are screen-reader-only (FR-057, SC-014)
 *
 * **Do NOT print them beside the controls** — reviewer decision 2026-09-08; across four disabled
 * controls it read as clutter. FR-057 still holds: `disabled` conveys unavailability visually *and*
 * programmatically, and the adjacent hidden region carries the *why*.
 *
 * **The cost, recorded so it is not rediscovered as a defect:** a hidden adjacent region is reached
 * by navigating **past** the control, not read **with** it. The direct mechanism does not exist —
 * Interstate's `ButtonProps` exposes **no `aria-describedby`**, and `aria-label` would *replace* the
 * button's accessible name with a sentence. Adding `aria-describedby` to the wrapper is a
 * `libs/core/components` API change benefiting every page, so it is a separate, independently
 * reviewable change and a known follow-up — not re-derived work.
 *
 * **Do not use a block `Alert` in this flex cluster.** It forces the cluster to the alert's width
 * and wraps the buttons — that happened on My Info. The pressure to reach for an `Alert` will recur.
 *
 * ## Two blocking dependencies, recorded as blockers rather than deferrals (D-18)
 *
 * 1. **No administrative password-reset endpoint exists** in the monorepo; the legacy gateway
 *    exposes one. This is why the dialog opens soft-disabled.
 * 2. **A selected user's bridge *status* is not resolved** — though its source now exists (revised
 *    2026-09-17). `useGetBridgeId` reads the per-user Bridge ID from CC9
 *    `/user-profile/{ccUsername}` for the identity header, which is what R-003 concluded did not
 *    exist. `isBridgeUser` is still hardcoded `false`, because deriving *status* from a present id
 *    gates a password action and should be ratified, not inferred — so **FR-056 is still honoured
 *    for nobody.** That remains a **Story 6** item, not a read-side one: the dialog can act for no
 *    subject and says so to every subject, so there is no bridge-specific state to distinguish and
 *    FR-056's trigger stays unreachable — the same argument My Info's `HeaderActions` makes for its
 *    FR-018. It still gates the mutation: wire `isBridgeUser` before the fields go live, or the page
 *    offers resets to bridge users it cannot reset. **Do not** paper over it by guessing from
 *    `platformId` (a GUID, observed null live) or a `userId` pattern.
 *
 * ## When the write endpoints land
 *
 * **Enabling these controls SHOULD be the only change required** (FR-083). The six save-behavior
 * requirements — FR-021, FR-026, FR-026a, FR-027, FR-027b, FR-028 — are **write-story scope** and
 * are to be **revisited rather than inherited**: two of them (FR-026a, FR-027b) were proposed during
 * clarification rather than drawn from a source (FR-083a). And FR-083b's prior question stands:
 * whether the upstream update route is even the intended write path for this page is **unverified**.
 *
 * FR-063's **self-delete rule** must hold whenever Delete becomes enabled: an operator MUST NOT be
 * able to delete their **own** record, and that MUST be conveyed with its reason rather than as a
 * merely inert control. It is not observable while Delete is disabled outright.
 */
export const HeaderActions = ({
  isSubjectSelected,
  isSubjectTheOperator = false,
  isDirty = false
}: HeaderActionsProps) => {
  const [isResetOpen, setIsResetOpen] = useState(false);
  const menuReferenceProps = useMenu();

  /*
   * Named `handle*` per the repo's `react/jsx-handler-names` rule. Interstate hands `onSelect` a
   * `MenuItemEvent`, which this ignores — the menu item's identity is already fixed by which option
   * carries the handler.
   */
  const handleResetPasswordSelected = () => {
    setIsResetOpen(true);
  };

  /*
   * Interim, and interim on purpose — reviewer decision 2026-09-23. There is no write endpoint yet,
   * so the button proves it is wired and does nothing else. `no-console` is a **warning** in this
   * repo and `lint` runs `--quiet`, so this does not fail CI; it is still a placeholder and should
   * be replaced by the mutation rather than joined by it.
   */
  const handleSave = () => {
    // eslint-disable-next-line no-console
    console.log("[UserManager] Save clicked — no write endpoint wired yet.");
  };

  /*
   * A named wrapper rather than `onClick={menuReferenceProps.onClick}`, which `react/jsx-handler-
   * names` rejects. `useMenu` needs the click event to position the menu against the trigger, so it
   * is forwarded rather than swallowed.
   */
  const handleOtherActionsClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    menuReferenceProps.onClick?.(event);
  };

  return (
    <Box
      data-testid={"usermanager-header-actions-content"}
      sx={{
        alignItems: "center",
        columnGap: "8px",
        display: "flex",
        flexWrap: "wrap",
        rowGap: "8px"
      }}
    >
      <Box sx={{ position: "relative", ...labelledActionsOnly("block") }}>
        <Button
          analyticsConfiguration={{
            eventName: "usermanager-reset-password",
            eventProperties: undefined
          }}
          buttonStyle={"tertiary"}
          data-testid={"usermanager-reset-password"}
          disabled={!isSubjectSelected}
          onClick={() => setIsResetOpen(true)}
        >
          {Locale.getText("RESET_PASSWORD") as string}
        </Button>

        {/* Only a reason when it IS disabled — an enabled control needs no explanation. */}
        {!isSubjectSelected && (
          <Box
            data-testid={"usermanager-reset-password-reason"}
            sx={SCREEN_READER_ONLY}
          >
            {Locale.getText("UM_RESET_PASSWORD_NO_SELECTION") as string}
          </Box>
        )}
      </Box>

      <Box sx={{ position: "relative", ...labelledActionsOnly("block") }}>
        <Button
          disabled
          analyticsConfiguration={{
            eventName: "usermanager-delete-user",
            eventProperties: undefined
          }}
          buttonStyle={"tertiary"}
          data-testid={"usermanager-delete"}
        >
          {Locale.getText("DELETE_USER") as string}
        </Button>

        <Box data-testid={"usermanager-delete-reason"} sx={SCREEN_READER_ONLY}>
          {Locale.getText("UM_DELETE_UNAVAILABLE") as string}
        </Box>
      </Box>

      {/*
       * ## The mobile overflow menu — the two secondary actions only
       *
       * Reset Password and Delete collapse here below `md`; **Save does not** (see the next block).
       * TA4236901's reasoning for moving Delete in particular is the point: a destructive action
       * next to a back affordance at thumb height is asking for accidents.
       *
       * The French labels settle it independently — `RESET_PASSWORD` is
       * "réinitialiser le mot de passe" (29 chars) and `DELETE_USER` is "Supprimer l'utilisateur"
       * (23). Neither can be a visible button at 390px; `SAVE` at "Enregistrer" (11) can.
       *
       * `OTHER_ACTIONS` names the trigger rather than `MENU`, which is already the word the navbar's
       * collapsed nav control puts on screen — two icon affordances both reading "Menu" on the same
       * narrow viewport is a real ambiguity, and "Menu" implies navigation where this holds actions.
       */}
      <Box sx={{ position: "relative", ...collapsedActionsOnly("block") }}>
        {/*
         * `Button` + `Menu` + `useMenu` rather than `core-components`' own `MenuButton`, for one
         * concrete reason: `MenuButton`'s `ButtonProps` is Interstate's **raw** `ButtonProps`, which
         * has no `analyticsConfiguration`, so routing the trigger through it would silently drop the
         * GTM event that every other control on this page emits. Composing the three pieces keeps
         * the repo's analytics `Button` — the same composition `MenuButton` performs internally, and
         * the same one `core/navbar`'s `accountSelector` uses.
         */}
        <Button
          analyticsConfiguration={{
            eventName: "usermanager-other-actions",
            eventProperties: undefined
          }}
          aria-haspopup={"true"}
          aria-label={Locale.getText("OTHER_ACTIONS") as string}
          buttonStyle={"tertiary"}
          data-testid={"usermanager-other-actions"}
          icon={<EllipsisVerticalIcon title={""} />}
          size={"lg"}
          onClick={handleOtherActionsClick}
        />

        <Menu
          menuOptions={[
            {
              disabled: !isSubjectSelected,
              id: "usermanager-menu-reset-password",
              label: Locale.getText("RESET_PASSWORD") as string,
              onSelect: handleResetPasswordSelected,
              value: "reset-password"
            },
            {
              disabled: true,
              id: "usermanager-menu-delete",
              label: Locale.getText("DELETE_USER") as string,
              value: "delete"
            }
          ]}
          referenceProps={menuReferenceProps.referenceProps}
        />

        {/*
         * ⚠️ **FR-057's reasons cannot ride on the menu items, so they ride here.**
         *
         * Interstate's `MenuOption` is `{ label?, disabled?, divider?, id?, value?, onSelect?,
         * icon? }` and **`label` is a plain `string`, not `ReactNode`** — there is no slot for a
         * nested element and no `aria-describedby`. So the per-control `SCREEN_READER_ONLY` sibling
         * the desktop buttons above use has nowhere to attach inside the menu.
         *
         * This block is the compromise: one region beside the trigger carrying the reason for every
         * disabled item. The association is looser than a per-control sibling — a screen reader
         * hears it with the trigger rather than with the item — but it is announced, it is not
         * printed, and it honours both FR-057 and the 2026-09-08 reviewer decision that the reasons
         * stay screen-reader-only.
         *
         * **Do NOT fold the reason into the `label` string.** That prints it beside the control,
         * which is precisely what that decision rules out (across four disabled controls it read as
         * clutter). Flagged to the reviewer as the one place the mobile layout weakens an existing
         * guarantee rather than preserving it.
         */}
        <Box
          data-testid={"usermanager-other-actions-reasons"}
          sx={SCREEN_READER_ONLY}
        >
          {Locale.getText("UM_DELETE_UNAVAILABLE") as string}
        </Box>
      </Box>

      {/*
       * ## Save stays a visible button at every width
       *
       * It is **unconditionally disabled** (FR-083 — no write endpoint), and an earlier draft of
       * TA4236901's plan therefore put it in the menu and deferred a sticky footer to the write
       * story. Reversed 2026-09-23, for two reasons:
       *
       * 1. **Desktop already ships a visible disabled Save.** Hiding it on mobile would make the two
       *    layouts disagree about what the page offers, which is worse than a quiet inactive
       *    control.
       * 2. **Save is the view's primary action.** Overflow menus are for secondary and destructive
       *    actions; burying the primary one inverts that. And leaving it in place means the write
       *    story enables a button rather than re-laying-out the header.
       *
       * The "dead UI" objection that motivated hiding it was really an objection to a **sticky
       * footer** — a permanent 56px band on the narrowest screen in the product. It does not
       * transfer to a ~74px button in a row that exists anyway.
       */}
      <Box sx={{ position: "relative" }}>
        <Button
          analyticsConfiguration={{
            eventName: "usermanager-save",
            eventProperties: undefined
          }}
          buttonStyle={"primary"}
          data-testid={"usermanager-save"}
          disabled={!isDirty}
          onClick={handleSave}
        >
          {Locale.getText("SAVE") as string}
        </Button>

        {/*
         * No screen-reader reason here, unlike Delete above. Save is inactive because the form
         * matches the stored record — the ordinary meaning of a disabled Save, which needs no
         * explanation. `UM_SAVE_UNAVAILABLE` ("Saving changes is not available yet") was removed
         * with this change rather than kept: it would be a false statement about a button that
         * works.
         */}
      </Box>

      <ResetPasswordModal
        isOpen={isResetOpen}
        isSubjectTheOperator={isSubjectTheOperator}
        isWriteAvailable={false}
        onClose={() => setIsResetOpen(false)}
        onConfirm={() => undefined}
      />
    </Box>
  );
};
